import { useState } from "react";

import rakutenLogo from "@assets/rakuten-logo.svg";
import { useUser } from "@components/user-context";
import { LearnMoreTooltip } from "@features/sales-v3/top-level-filters/components/learn-more-tooltip";
import { ConnectionType } from "@north-beam/nb-client-side";
import { ConnectionCard } from "@pages/settings/connection-card";
import {
  type LoginComponentChildrenProps,
  type LoginComponentProps,
} from "@pages/settings/connection-status-manager";
import { PrimaryButton } from "@shared/buttons";
import { Switch } from "@shared/switch";
import TextBoxWithCopyButton from "@shared/text-box-with-copy-button";

const type = ConnectionType.integration_rakuten;

export function RakutenConnectionCard() {
  return (
    <ConnectionCard
      type={type}
      connectionTypeName="Rakuten"
      connectionCallToAction="Integrate your Rakuten account"
      logoUrl={rakutenLogo}
      logoAltText="Rakuten Logo"
      successfulConnectionExtraText={() => null}
      knowledgeBaseLink="https://docs.northbeam.io/docs/tracking-for-rakuten"
    >
      {({
        upsertConnection,
        isLoading,
        accounts,
      }: LoginComponentChildrenProps) => (
        <RakutenLoginComponent
          upsertConnection={upsertConnection}
          isLoading={isLoading}
          accounts={accounts}
        />
      )}
    </ConnectionCard>
  );
}

function RakutenLoginComponent({
  upsertConnection,
  isLoading,
}: Omit<LoginComponentProps, "setStatus" | "integrationDetails">) {
  const [hasConfiguredUTMs, setHasConfiguredUTMs] = useState(false);
  const [hasConfiguredScheduledReport, setHasConfiguredScheduledReport] =
    useState(false);

  const { user } = useUser();
  const onConnectClicked = async () => {
    await upsertConnection({});
  };

  return (
    <div className="mb-3">
      <div className="flex flex-col gap-y-5 mt-5">
        <div className="flex items-center gap-2">
          <Switch
            id="rakuten-utms-configuration"
            size="small"
            value={hasConfiguredUTMs}
            onChange={(updatedValue) => setHasConfiguredUTMs(updatedValue)}
            disabled={isLoading}
          />
          <div>
            I finished configuring my Click UTMs
            <LearnMoreTooltip
              title="Click UTMs Configuration"
              content="Be sure to configure your Click UTMs before connecting your Rakuten account"
              link="https://docs.northbeam.io/docs/tracking-for-rakuten#step-1-inserting-click-utms"
            />
          </div>
        </div>
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Switch
              id="rakuten-scheduled-report"
              size="small"
              value={hasConfiguredScheduledReport}
              onChange={(value) => setHasConfiguredScheduledReport(value)}
              disabled={isLoading}
            />
            <div>
              I created a Scheduled Report to the email below:
              <LearnMoreTooltip
                title="Scheduled Report"
                content="Be sure to create a Scheduled Report before connecting your Rakuten account"
                link="https://docs.northbeam.io/docs/tracking-for-rakuten#step-2-creating-a-scheduled-report"
              />
            </div>
          </div>
          <TextBoxWithCopyButton
            className="pl-8 max-w-xl"
            text={`${user.displayUserId}zzznbzzzrakuten@robot.northbeam.io`}
            textCopiedNotificationMessage="Email copied!"
          />
        </div>
        <div>
          <PrimaryButton
            loading={isLoading}
            disabled={!hasConfiguredUTMs || !hasConfiguredScheduledReport}
            onClick={onConnectClicked}
          >
            {isLoading ? "Please Wait..." : "CONNECT"}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}
